import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { criarLeadEcossistemaCore } from '@/hooks/useCriarLeadDoEcossistema';

// ── Persistência local da última consulta ─────────────────────────────────
// Mantém o último resultado de cada CNPJ/CPF/NÚMERO em localStorage para
// que ao trocar de aba e voltar, o dossiê não desapareça. Limite de 30
// consultas (FIFO) para não estourar storage.
const STORAGE_KEY = 'ecossistema_ultimas_consultas_v1';
const MAX_CACHE_ITEMS = 30;

interface ConsultaCacheItem {
  tipo: string;
  valor: string;
  data: any;
  consultadoEm: number;
}

function lerCache(): ConsultaCacheItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function gravarCache(items: ConsultaCacheItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_CACHE_ITEMS)));
  } catch {
    // localStorage cheio ou indisponível — silencia
  }
}

/** Recupera a última consulta de um (tipo, valor) específico. */
export function lerUltimaConsulta(tipo: string, valor: string): any | null {
  const cleanValor = String(valor || '').replace(/\D/g, '');
  if (!cleanValor) return null;
  const items = lerCache();
  const found = items.find(
    (i) =>
      i.tipo === tipo &&
      String(i.valor).replace(/\D/g, '') === cleanValor &&
      Date.now() - i.consultadoEm < 24 * 60 * 60 * 1000 // 24h
  );
  return found?.data || null;
}

/** Recupera a consulta MAIS RECENTE de um tipo (independente do valor). */
export function lerUltimaConsultaPorTipo(tipo: string): { valor: string; data: any } | null {
  const items = lerCache();
  const filtered = items
    .filter((i) => i.tipo === tipo && Date.now() - i.consultadoEm < 24 * 60 * 60 * 1000)
    .sort((a, b) => b.consultadoEm - a.consultadoEm);
  if (filtered.length === 0) return null;
  return { valor: filtered[0].valor, data: filtered[0].data };
}

function salvarConsulta(tipo: string, valor: string, data: any) {
  const items = lerCache();
  const cleanValor = String(valor || '').replace(/\D/g, '');
  // Remove duplicata anterior do mesmo (tipo, valor)
  const filtered = items.filter(
    (i) => !(i.tipo === tipo && String(i.valor).replace(/\D/g, '') === cleanValor)
  );
  filtered.push({ tipo, valor: cleanValor, data, consultadoEm: Date.now() });
  gravarCache(filtered);
}

export type EcossistemaTipo = 'CNPJ' | 'CPF' | 'NUMERO';

export interface EcossistemaConsultaInput {
  tipo: EcossistemaTipo;
  valor: string;
  options?: {
    force_refresh?: boolean;
    reverso?: boolean;
  };
}

export interface CascataStatus {
  receita?: 'ok' | 'erro' | 'not_found';
  assertiva?: 'ok' | 'erro';
  linhas_internas?: 'ok' | 'erro';
  operadora?: 'ok' | 'erro' | 'sem_telefones' | 'invalido';
  cobertura?: 'ok' | 'tabela_indisponivel' | 'sem_cep';
  reverso?: 'ok' | 'nao_encontrado';
}

export interface EcossistemaConsultaResult {
  ok: boolean;
  consulta_id?: string;
  tipo: EcossistemaTipo;
  cascata_status: CascataStatus;
  // Específicos por tipo (campos opcionais conforme retorno do orquestrador)
  cnpj?: string;
  cpf?: string;
  numero?: string;
  receita?: any;
  assertiva?: any;
  /**
   * Sócios enriquecidos: cada item tem `socio_original` (vindo da Assertiva CNPJ)
   * e `enriquecimento` (resposta da Assertiva CPF do sócio com endereço,
   * telefones, e-mails, idade etc). Pode ser null se o sócio não tem CPF
   * válido ou a consulta falhou.
   */
  socios_enriquecidos?: Array<{
    socio_original: any;
    enriquecimento: any | null;
  }>;
  cep_matriz_consultado?: string | null;
  /**
   * Origem de cada telefone consultado.
   * Chave: número normalizado (55DDD...). Valor: { origem, detalhe }.
   * origem: 'empresa' | 'crm' | 'socio'
   */
  telefones_origem?: Record<string, { origem: string; detalhe?: string }>;
  /**
   * Cobertura BL por CEP — inclui matriz, endereços secundários da empresa
   * e endereço pessoal de cada sócio.
   * Chave: CEP de 8 dígitos. Valor: { ativo, tipo_servico, velocidade_max_mbps, contexto }.
   */
  cobertura_por_cep?: Record<
    string,
    {
      ativo: boolean;
      tipo_servico?: string | null;
      velocidade_max_mbps?: number | null;
      contexto: string;
    }
  >;
  linhas_internas?: Array<{ telefone: string; operadora: string | null }>;
  operadoras?: Array<{ NÚMERO: string; OPERADORA: string; 'DATA PORTABILIDADE': string; FONTE?: string }>;
  operadora?: any;
  cnpj_vinculado?: string | null;
  cnpjs_vinculados?: string[];
  tem_cobertura_bl?: boolean | null;
  score?: number | null;
  contadores?: {
    total_telefones?: number;
    total_celulares?: number;
    total_celulares_nao_claro?: number;
    tem_celular_nao_claro?: boolean;
    tem_whatsapp?: boolean;
  };
  erro?: string;
}

/**
 * Hook que dispara o orquestrador do Ecossistema API e retorna o resultado
 * agregado (Receita + Assertiva + Operadora + Cobertura). Usado pelas abas
 * CNPJ/CPF/NUMERO e pelo botão "Re-enriquecer" do dossiê.
 */
export function useEcossistemaApi() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: EcossistemaConsultaInput): Promise<EcossistemaConsultaResult> => {
      console.log('[Ecossistema] orquestrador — request:', input);
      const { data, error } = await supabase.functions.invoke('ecossistema-api-orquestrador', {
        body: input,
      });
      console.log('[Ecossistema] orquestrador — response:', { error, data });

      // Erro de invocação (network, 4xx/5xx, edge não deployada)
      if (error) {
        const msg =
          error.message?.includes('Function not found') || error.message?.includes('404')
            ? 'Edge ecossistema-api-orquestrador ainda não foi deployada. Faça deploy das edge functions.'
            : error.message || 'Erro no orquestrador';
        throw new Error(msg);
      }
      // Edge respondeu mas com falha lógica
      if (data?.ok === false) {
        throw new Error(data?.error || 'Orquestrador retornou ok=false sem detalhe');
      }
      if (!data) {
        throw new Error('Orquestrador retornou resposta vazia');
      }
      return data as EcossistemaConsultaResult;
    },
    onSuccess: (data, vars) => {
      // 1) Salva em localStorage — sobrevive a trocas de aba
      salvarConsulta(vars.tipo, vars.valor, data);
      // 2) Detecta persist_erro retornado pela edge: cascata rodou mas o
      //    INSERT em ecossistema_consultas falhou (RLS, NaN, FK, etc).
      //    Antes esse caso era silenciado e a consulta nunca aparecia em
      //    "Consultas Salvas". Agora gera toast amarelo explicando o motivo.
      const persistErro = (data as any)?.persist_erro;
      if (persistErro) {
        console.warn('[Ecossistema] cascata OK mas persist falhou:', persistErro);
        toast({
          title: 'Consulta NÃO salva em Consultas Salvas',
          description: `Os dados foram exibidos mas não foram persistidos. Motivo: ${persistErro}`,
          variant: 'destructive',
        });
      } else if (!(data as any)?.consulta_id) {
        // Edge esqueceu de retornar consulta_id (não deveria acontecer com
        // a versão nova) — mantemos warning para diagnóstico.
        console.warn('[Ecossistema] consulta_id ausente na resposta — verificar edge');
      }
      // 3) Invalida a lista da aba "Consultas Salvas" com refetchType:'all'
      //    para forçar refetch mesmo em queries inativas (aba não montada).
      qc.invalidateQueries({ queryKey: ['consultas-salvas'], refetchType: 'all' });
      qc.invalidateQueries({ queryKey: ['dossie-cnpj'], refetchType: 'all' });

      // 4) AUTO-VIRA-LEAD: toda consulta CNPJ bem-sucedida cria/atualiza um
      //    lead em leads_carteira com tipo_lead='ECOSSISTEMA_API', acoplado
      //    à campanha mensal "Ecossistema API — YYYY-MM". Idempotente via
      //    UPSERT em (cnpj, mes_referencia) — re-consultar não duplica, só
      //    atualiza dados de contato. Silencioso (sem toast / sem navegação)
      //    porque é background — o user vê o dossiê normalmente.
      //
      //    Conforme plano original (Passo 3): toda consulta vira lead
      //    automaticamente para alimentar a campanha mensal sem clique manual.
      if (vars.tipo === 'CNPJ' && (data as any)?.cnpj) {
        const result = data as EcossistemaConsultaResult;
        const r: any = result.receita || {};
        const est: any = r.estabelecimento || {};
        // Telefone preferido: celular não-Claro se houver, senão primeiro do
        // payload de operadoras. Email preferido: e-mail Receita Federal.
        const operadoras: any[] = result.operadoras || [];
        const naoClaro = operadoras.find((o: any) => {
          const op = String(o.OPERADORA || '').toUpperCase();
          const digits = String(o['NÚMERO'] || '').replace(/\D/g, '');
          return digits.length >= 12 && !['CLARO', 'ERRO', 'DESCONHECIDO', 'NÃO LOCALIZADO'].includes(op);
        });
        const melhorTelefone = naoClaro?.['NÚMERO'] || operadoras[0]?.['NÚMERO'] || null;
        const melhorEmail =
          est.email || result.assertiva?.emails?.[0]?.email || null;

        criarLeadEcossistemaCore({
          consultaId: result.consulta_id || null,
          cnpj: result.cnpj || vars.valor,
          razaoSocial: r.razao_social || result.assertiva?.razaoSocial || `Cliente ${vars.valor}`,
          uf: est.estado?.sigla || null,
          telefone: melhorTelefone,
          email: melhorEmail,
          silent: true,
        })
          .then((res) => {
            // Sucesso: lead criado em "Ecossistema API — YYYY-MM" OU lead
            // existente em ECOSSISTEMA_API + mesmo mês foi atualizado (UPSERT).
            // A regra CRM "1 lead por CNPJ POR CAMPANHA" é garantida pela
            // constraint física idx_leads_campanha_cnpj_aberto + UPSERT em
            // (cnpj, tipo_lead, mes_referencia). NÃO impede leads do mesmo
            // CNPJ em outras campanhas (PORT, REN etc.) — cada campanha tem
            // seu próprio lead.
            console.info('[Ecossistema] auto-vira-lead OK', {
              leadId: res.leadId,
              campanhaId: res.campanhaId,
              cnpj: result.cnpj,
            });
            qc.invalidateQueries({ queryKey: ['leads-carteira'] });
            qc.invalidateQueries({ queryKey: ['gestao-leads'] });
            qc.invalidateQueries({ queryKey: ['campanhas-prospeccao-ativas'] });
          })
          .catch((e: any) => {
            // SEMPRE mostra toast quando auto-vira-lead falha — antes era
            // silencioso para erros não-estruturais, mas isso escondia
            // problemas reais (consulta salvava, lead não criava, user não
            // sabia). Agora qualquer erro vira toast destrutivo com código
            // Postgres para diagnóstico imediato.
            console.error('[Ecossistema] auto-vira-lead FALHOU:', e);
            const msg = e?.message || e?.error || String(e) || 'erro desconhecido';
            const code = e?.code || (e as any)?.details || '';
            toast({
              title: 'Lead NÃO foi criado em /gestao-leads',
              description: `${code ? `[${code}] ` : ''}${msg}. A consulta foi exibida normalmente, mas o lead não entrou na campanha mensal — clique em "Virar lead" na aba Consultas Salvas para tentar manualmente, ou reporte o erro acima.`,
              variant: 'destructive',
            });
          });
      }
    },
    onError: (err: any) => {
      toast({
        title: 'Erro na consulta',
        description: err?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });

  return {
    consultar: mutation.mutate,
    consultarAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    data: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
}
